// @ts-nocheck
import React, { forwardRef } from 'react';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-xs',
  sm: 'h-8 w-8 text-sm',
  md: 'h-10 w-10 text-base',
  lg: 'h-12 w-12 text-lg',
  xl: 'h-16 w-16 text-xl',
};

interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  alt?: string;
  name?: string;
  size?: AvatarSize;
  shape?: 'circle' | 'square';
  fallback?: React.ReactNode;
  status?: 'online' | 'offline' | 'busy' | 'away';
  statusPosition?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  className?: string;
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const colorFromName = (name: string) => {
  const colors = [
    'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
    'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
    'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300',
    'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300',
    'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300',
    'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300',
    'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300',
    'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
    'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900 dark:text-fuchsia-300',
    'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
    'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300',
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ src, alt, name, size = 'md', shape = 'circle', fallback, status, statusPosition = 'bottom-right', className, ...props }, ref) => {
    const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg';
    const sizeClass = sizeClasses[size];
    const statusSizes = { xs: 'h-2 w-2', sm: 'h-2.5 w-2.5', md: 'h-3 w-3', lg: 'h-3.5 w-3.5', xl: 'h-4 w-4' };
    
    const statusColors = {
      online: 'bg-green-500',
      offline: 'bg-gray-400',
      busy: 'bg-red-500',
      away: 'bg-yellow-500',
    };

    const statusBorderColors = {
      online: 'border-green-500',
      offline: 'border-gray-400',
      busy: 'border-red-500',
      away: 'border-yellow-500',
    };

    const positionClasses = {
      'bottom-right': 'bottom-0 right-0',
      'bottom-left': 'bottom-0 left-0',
      'top-right': 'top-0 right-0',
      'top-left': 'top-0 left-0',
    };

    const hasImage = src && src.trim() !== '';
    const displayName = name || alt || 'User';
    const initials = getInitials(displayName);
    const bgColor = name ? colorFromName(name) : 'bg-bg-tertiary text-text-secondary';

    return (
      <div ref={ref} className={`relative inline-flex shrink-0 ${shapeClass} ${sizeClass} ${className || ''}`} {...props}>
        {hasImage ? (
          <img
            src={src}
            alt={alt || displayName}
            className={`${shapeClass} h-full w-full object-cover`}
            loading="lazy"
          />
        ) : (
          <div
            className={`${shapeClass} h-full w-full flex items-center justify-center font-medium ${bgColor}`}
            aria-label={displayName}
          >
            {fallback || initials}
          </div>
        )}
        {status && (
          <span
            className={`absolute ${positionClasses[statusPosition]} ${statusSizes[size]} rounded-full border-2 border-bg-primary ${statusColors[status]}`}
            aria-label={`Status: ${status}`}
          />
        )}
      </div>
    );
  }
);
Avatar.displayName = 'Avatar';

interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  avatars: Array<{ src?: string; name?: string; alt?: string; size?: AvatarSize }>;
  max?: number;
  size?: AvatarSize;
  spacing?: number;
}

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  avatars,
  max = 5,
  size = 'md',
  spacing = -8,
  className,
  ...props
}) => {
  const visibleAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;

  return (
    <div className={`flex -space-x-${Math.abs(spacing)} ${className || ''}`} {...props}>
      <>
        {visibleAvatars.map((avatar, index) => (
          <Avatar
            key={index}
            src={avatar.src}
            name={avatar.name}
            alt={avatar.alt}
            size={avatar.size || size}
            className="ring-2 ring-bg-primary"
            style={{ zIndex: max - index }}
          />
        ))}
        {remainingCount > 0 && (
          <div
            className={`flex items-center justify-center ${sizeClasses[size]} rounded-full bg-bg-tertiary text-text-secondary font-medium ring-2 ring-bg-primary`}
            style={{ marginLeft: spacing }}
          >
            +{remainingCount}
          </div>
        )}
      </>
    </div>
  );
};

interface AvatarStackProps extends React.HTMLAttributes<HTMLDivElement> {
  avatars: Array<{ src?: string; name?: string; alt?: string }>;
  max?: number;
  size?: AvatarSize;
  className?: string;
}

export const AvatarStack: React.FC<AvatarStackProps> = ({
  avatars,
  max = 4,
  size = 'md',
  className,
  ...props
}) => {
  const visibleAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;

  return (
    <div className={`flex ${className || ''}`} {...props}>
      {visibleAvatars.map((avatar, index) => (
        <Avatar
          key={index}
          src={avatar.src}
          name={avatar.name}
          alt={avatar.alt}
          size={size}
          className={index > 0 ? '-ml-2 ring-2 ring-bg-primary' : ''}
        />
      ))}
      {remainingCount > 0 && (
        <Avatar
          size={size}
          name={`+${remainingCount}`}
          className="-ml-2 ring-2 ring-bg-primary bg-bg-tertiary text-text-secondary"
        />
      )}
    </div>
  );
};

export interface UserAvatarProps {
  user: { name: string; email?: string; avatar?: string };
  size?: AvatarSize;
  showStatus?: boolean;
  status?: 'online' | 'offline' | 'busy' | 'away';
  className?: string;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = 'md',
  showStatus = false,
  status,
  className,
}) => (
  <Avatar
    src={user.avatar}
    name={user.name}
    alt={user.name}
    size={size}
    status={showStatus ? status : undefined}
    className={className}
  />
);